import React, { createContext, useContext, useState, useCallback } from 'react';
import { DeleteDialog } from './DeleteDialog';

interface DialogContextType {
  dialogData: DialogData | null;
  openDialog: (data: DialogData) => Promise<DialogResult>;
  closeDialog: (result: DialogResult) => void;
}

interface DialogData {
  id?: string;
  title: string;
  message: string;
  btnConfirm?: string;
  btnCancel?: string;
  onConfirm?: () => Promise<void>;
  onCancel?: () => void;
}

interface DialogResult {
  confirmed: boolean;
  id?: string;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDeleteDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export const DeleteDialogProvider: React.FC = ({ children }) => {
  const [dialogData, setDialogData] = useState<DialogData | null>(null);
  const [resolveDialog, setResolveDialog] = useState<((result: DialogResult) => void) | null>(null);

  const openDialog = useCallback((data: DialogData): Promise<DialogResult> => {
    setDialogData(data);
    return new Promise<DialogResult>(resolve => {
      setResolveDialog(() => resolve);
    });
  }, []);

  const closeDialog = useCallback(
    (result: DialogResult) => {
      if (resolveDialog) {
        resolveDialog(result);
        setDialogData(null);
        setResolveDialog(null);
      }
    },
    [resolveDialog],
  );

  return (
    <DialogContext.Provider value={{ dialogData, openDialog, closeDialog }}>
      <DeleteDialog />
      {children}
    </DialogContext.Provider>
  );
};
